import moment from "moment";

const defaultTimezone = "America/Chicago";

const calculateTotalTime = (inTime, outTime, units) => {
  if (!inTime || !outTime) return 0;
  const dateIn = new Date(inTime).toISOString().split("T")[0];
  const minsIn = new Date(inTime).toISOString().split("T")[1];
  const minsOut = new Date(outTime).toISOString().split("T")[1];
  let totalMs;
  let nextDay;
  if (new Date(`${dateIn}T${minsOut}`) > new Date(`${dateIn}T${minsIn}`))
    totalMs =
      new Date(`${dateIn}T${minsOut}`).getTime() - new Date(`${dateIn}T${minsIn}`).getTime();
  else {
    // eslint-disable-next-line no-extra-semi
    [nextDay] = new Date(new Date(dateIn).getTime() + 24 * 60 * 60 * 1000).toISOString().split("T");
    totalMs =
      new Date(`${nextDay}T${minsOut}`).getTime() - new Date(`${dateIn}T${minsIn}`).getTime();
  }
  return units === "mins"
    ? Math.round(totalMs / 1000 / 60)
    : Math.round((totalMs / 1000 / 60 / 60) * 100) / 100;
};

export const calculateTotalHours = (row, tz) => {
  const timezone = tz ?? defaultTimezone;

  // Check if there's local data
  const timeIn = row?.rosterData?.timeIn;
  const timeOut = row?.rosterData?.timeOut;

  if (!timeIn || !timeOut) return 0;

  const minsIn = timeIn ? moment(timeIn).tz(timezone).format("HH:mm:ss") : null;
  const minsOut = timeOut ? moment(timeOut).tz(timezone).format("HH:mm:ss") : null;

  const now = moment(new Date()).tz(timezone);
  const nowDatePart = now.format("YYYY-MM-DD");
  const nowOffset = now.format("ZZ");

  const dateInAdjusted = minsIn ? new Date(`${nowDatePart}T${minsIn}${nowOffset}`) : null;
  const dateOutAdjusted = minsOut ? new Date(`${nowDatePart}T${minsOut}${nowOffset}`) : null;
  const hours =
    dateInAdjusted && dateOutAdjusted
      ? calculateTotalTime(dateInAdjusted, dateOutAdjusted, "hrs")
      : "";
  return hours;
};

export const getWeekInTwoWeekPeriodForDate = (date, shiftWeek = false, weekStartDay = 0) => {
  const targetDate = moment(date);
  const epochStart = moment.unix(0).add(weekStartDay + 3, "days");

  const epochWeekday = epochStart.isoWeekday();

  let weeksSinceEpoch = targetDate.diff(epochStart, "weeks");

  if (shiftWeek) {
    weeksSinceEpoch += 1;
  }

  const weekNumber = (weeksSinceEpoch % 2) + 1;

  return {
    date: targetDate.toISOString().split("T")[0],
    weekNumber,
    isFirstWeek: weekNumber === 1,
    isSecondWeek: weekNumber === 2,
  };
};

export const getPeriodForDate = (date, periodLength, shiftWeek = false, weekStartDay = 0) => {
  const todayMoment = moment(date).tz(defaultTimezone);

  // WEEKLY
  if (periodLength === "weekly") {
    if (weekStartDay <= todayMoment.day()) {
      const startDate = todayMoment.clone().day(weekStartDay).startOf("day");
      const endDate = startDate.clone().add(6, "days").endOf("day");
      return [startDate.toDate(), endDate.toDate()];
    }
    const startDate = todayMoment.clone().add(-1, "week").day(weekStartDay).startOf("day");
    const endDate = startDate.clone().add(6, "days").endOf("day");
    return [startDate.toDate(), endDate.toDate()];
  }

  // BI-WEEKLY
  if (periodLength === "biweekly") {
    const currentWeekOfPeriod = getWeekInTwoWeekPeriodForDate(date, shiftWeek, weekStartDay);
    if (weekStartDay <= todayMoment.day()) {
      let startDate = todayMoment.clone().day(weekStartDay);
      let endDate = startDate.clone().add(6, "days");

      if (currentWeekOfPeriod?.weekNumber === 2) {
        startDate.add(-1, "week");
      } else {
        endDate.add(1, "week");
      }

      startDate = startDate.startOf("day");
      endDate = endDate.endOf("day");

      return [startDate.toDate(), endDate.toDate()];
    }

    let startDate = todayMoment.clone().add(-1, "week").day(weekStartDay);
    let endDate = startDate.clone().add(6, "days");

    if (currentWeekOfPeriod?.weekNumber === 2) {
      startDate.add(-1, "week");
    } else {
      endDate.add(1, "week");
    }

    startDate = startDate.startOf("day");
    endDate = endDate.endOf("day");

    return [startDate.toDate(), endDate.toDate()];
  }

  // SEMI-MONTHLY
  if (periodLength === "semimonthly") {
    const firstPeriodStartDate = todayMoment.clone().startOf("month");
    const firstPeriodEndDate = firstPeriodStartDate.clone().add(14, "days").endOf("day");
    const secondPeriodStartDate = firstPeriodEndDate.clone().add(1, "hour").startOf("day");
    const secondPeriodEndDate = todayMoment.clone().endOf("month");

    if (todayMoment.isSameOrBefore(firstPeriodEndDate)) {
      return [firstPeriodStartDate.toDate(), firstPeriodEndDate.toDate()];
    }

    return [secondPeriodStartDate.toDate(), secondPeriodEndDate.toDate()];
  }

  // MONTHLY
  if (periodLength === "monthly") {
    const startDate = todayMoment.clone().startOf("month");
    const endDate = startDate.clone().endOf("month");

    return [startDate.toDate(), endDate.toDate()];
  }

  return [null, null];
};
